import { isAxiosError } from 'axios';

/** RFC 7807 ProblemDetails / API-feil med title (samme form som notificationsStore.addProblem). */
export interface ProblemDetailsBody {
  title: string;
  detail?: string;
  errors?: Record<string, string[]> | string[];
}

export function getProblemFromAxiosError (error: unknown): ProblemDetailsBody | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data;
  if (data === null || data === undefined || typeof data !== 'object') return null;
  const title = (data as { title?: unknown }).title;
  if (typeof title !== 'string' || title.trim().length === 0) return null;
  return data as ProblemDetailsBody;
}

/**
 * Bug 2841: axios' transport-feil ved manglende svar ('Network Error') er ubrukelig for
 * sluttbruker. Gjenkjenn network-level-feil (kall sendt, ingen response — server nede/restarter,
 * nettet nede) og gi en norsk, handlingsrettet melding. Returnerer null for alt annet: server
 * svarte → bruk serverens forklaring; bevisst avbrutt kall (ERR_CANCELED) → kalleren håndterer.
 *
 * Detektoren er `!error.response` (ikke `code === 'ERR_NETWORK'`) fordi et axios-feilobjekt uten
 * response per definisjon er «kall sendt, intet svar»; `code` er ikke alltid satt.
 */
export function humanizeNetworkError (error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  if (error.response) return null;
  if (error.code === 'ERR_CANCELED') return null;
  return 'Fikk ikke kontakt med serveren. Vent litt og prøv igjen – den kan være opptatt eller nettopp startet på nytt.';
}

// PrimaryTypeChangeResponse: warningMessage vinner over alt annet i kroppen.
function extractWarningMessage (responseData: object): string | null {
  if ('warningMessage' in responseData && typeof responseData.warningMessage === 'string') {
    return responseData.warningMessage;
  }
  return null;
}

// Valideringsfeil (ProblemDetails errors-objekt): slå sammen alle ikke-tomme meldinger.
function extractValidationErrors (responseData: object): string | null {
  if ('errors' in responseData && typeof responseData.errors === 'object' && responseData.errors !== null) {
    const errorMessages = Object.values(responseData.errors)
      .flat()
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0);

    if (errorMessages.length > 0) {
      return errorMessages.join('. ');
    }
  }
  return null;
}

// ProblemDetails detail-felt.
function extractDetail (responseData: object): string | null {
  const detail = (responseData as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }
  return null;
}

// ProblemDetails title-felt.
function extractTitle (responseData: object): string | null {
  const title = (responseData as { title?: unknown }).title;
  if (typeof title === 'string' && title.trim().length > 0) {
    return title;
  }
  return null;
}

// Serverens forklaring i prioritert rekkefølge: warningMessage → valideringsfeil → detail → title.
// Første som gir en melding vinner (samme rekkefølge og fall-through som den opprinnelige if-kjeden).
const responseMessageExtractors: Array<(responseData: object) => string | null> = [
  extractWarningMessage,
  extractValidationErrors,
  extractDetail,
  extractTitle
];

function extractMessageFromResponseData (responseData: unknown): string | null {
  if (!responseData || typeof responseData !== 'object') return null;
  for (const extractor of responseMessageExtractors) {
    const message = extractor(responseData);
    if (message !== null) return message;
  }
  return null;
}

/**
 * Ekstraherer feilmelding fra error-objekt med serverens forklaring først:
 * valideringsfeil/warningMessage → detail → title → transportmelding (error.message)
 * → fallback. Type-feltet kreves ikke i kroppen — ikke alle produsenter setter det.
 * Håndterer ProblemDetails, PrimaryTypeChangeResponse og generiske feil.
 */
export function extractErrorMessage (error: unknown, fallback: string = 'Feil ved lagring'): string {
  // Håndter ikke-Axios feil
  if (!isAxiosError(error)) {
    return error instanceof Error && error.message.trim().length > 0 ? error.message : fallback;
  }

  const serverMessage = extractMessageFromResponseData(error.response?.data);
  if (serverMessage !== null) return serverMessage;

  // Bug 2841: kall sendt, men ingen response fra serveren → handlingsrettet melding i stedet
  // for rå transport-sjargong («Network Error»).
  const networkMessage = humanizeNetworkError(error);
  if (networkMessage) return networkMessage;

  // Serveren sa ikke mer — transportmeldingen som siste utvei før fallback
  return error.message.trim().length > 0 ? error.message : fallback;
}

/**
 * Ekstraherer feltvise valideringsfeil fra en ProblemDetails-respons (errors-objektet), nøklet
 * på camelCase-feltnavn uavhengig av om serveren sender PascalCase eller camelCase. Returnerer
 * null når responsen ikke bærer feltfeil — bruk da extractErrorMessage for en samlet melding.
 */
export function extractFieldErrors (error: unknown): Record<string, string> | null {
  if (!isAxiosError(error)) return null;
  const responseData = error.response?.data;
  if (!responseData || typeof responseData !== 'object') return null;

  const errors = (responseData as { errors?: unknown }).errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;

  const result: Record<string, string> = {};
  for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
    const messages = (Array.isArray(value) ? value : [value])
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0);
    if (messages.length === 0) continue;
    result[field.charAt(0).toLowerCase() + field.slice(1)] = messages.join(', ');
  }
  return Object.keys(result).length > 0 ? result : null;
}

